import { useParams } from "react-router-dom";
import { Plus, Tag, GripVertical, Settings2, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { loadTicketEngine, saveTicketEngine, syncTicketEngine } from "@/lib/ticket-engine";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";

const INPUT_CLASS = "input-dark";
const CARD_CLASS = "rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)]";

export default function Tickets() {
  const { id } = useParams();
  const e = useEventMeta(id);
  const [state, setState] = useState(() => ({ tickets: [], promoCodes: [], purchases: [] }));
  const [tierName, setTierName] = useState("");
  const [trackName, setTrackName] = useState("");
  const [trackCode, setTrackCode] = useState("");
  const [allowCrossSell, setAllowCrossSell] = useState(false);
  const [itemName, setItemName] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoPercent, setPromoPercent] = useState("10");
  const [promoLimit, setPromoLimit] = useState("");
  const [promoStartsAt, setPromoStartsAt] = useState("");
  const [promoEndsAt, setPromoEndsAt] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [draggingTierId, setDraggingTierId] = useState(null);
  const [dropTierId, setDropTierId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [syncError, setSyncError] = useState("");
  const safeTickets = useMemo(() => (Array.isArray(state.tickets) ? state.tickets : []), [state.tickets]);
  const safePromoCodes = useMemo(() => (Array.isArray(state.promoCodes) ? state.promoCodes : []), [state.promoCodes]);
  const safePurchases = useMemo(() => (Array.isArray(state.purchases) ? state.purchases : []), [state.purchases]);

  useEffect(() => {
    setIsLoading(true);
    setSyncError("");
    setState(loadTicketEngine(e.id));
    syncTicketEngine(e.id)
      .then(() => setState(loadTicketEngine(e.id)))
      .catch(() => setSyncError("Could not sync latest ticket engine from server."))
      .finally(() => setIsLoading(false));
  }, [e.id]);

  useEffect(() => {
    if (!dirty) return;
    const t = window.setTimeout(() => setDirty(false), 900);
    return () => window.clearTimeout(t);
  }, [dirty]);

  const tiers = useMemo(() => safeTickets.filter((t) => t.type === "TIER"), [safeTickets]);
  const tracks = useMemo(() => safeTickets.filter((t) => t.type === "TRACK"), [safeTickets]);
  const items = useMemo(() => safeTickets.filter((t) => t.type === "ITEM"), [safeTickets]);
  const totalSold = useMemo(() => safeTickets.reduce((s, t) => s + Number(t.sold || 0) * Number(t.price || 0), 0), [safeTickets]);
  const totalIssued = useMemo(() => safeTickets.reduce((s, t) => s + Number(t.sold || 0), 0), [safeTickets]);
  const purchaseCount = useMemo(() => safePurchases.length, [safePurchases]);
  const liveGross = useMemo(() => {
    const priceMap = new Map(safeTickets.map((t) => [t.id, Number(t.price || 0)]));
    return safePurchases.reduce((sum, p) => {
      const explicit = Number(p?.lineTotal ?? p?.unitPrice);
      if (Number.isFinite(explicit)) return sum + explicit;
      return sum + (priceMap.get(p?.ticketId) || 0);
    }, 0);
  }, [safePurchases, safeTickets]);
  const totalQuota = useMemo(() => safeTickets.reduce((s, t) => s + Number(t.quota || 0), 0), [safeTickets]);
  const liveSellThrough = Math.round((purchaseCount / Math.max(1, totalQuota)) * 100);
  const seededBaseline = purchaseCount === 0 && totalIssued > 0;

  const persist = (next) => {
    if (isLoading) {
      toast.error("Please wait for ticket engine sync to finish.");
      return;
    }
    setState(next);
    saveTicketEngine(e.id, next).catch((err) => {
      setSyncError(String(err?.message || err || "Could not persist ticket engine."));
      toast.error("Save failed", { description: "Changes are local only until sync succeeds." });
    });
    setDirty(true);
  };

  const updateTicket = (ticketId, patch) => {
    persist({
      ...state,
      tickets: state.tickets.map((t) => (t.id === ticketId ? { ...t, ...patch } : t)),
    });
  };

  const reorderByIds = (type, sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const typeTickets = state.tickets.filter((t) => t.type === type);
    const sourceIndex = typeTickets.findIndex((t) => t.id === sourceId);
    const targetIndex = typeTickets.findIndex((t) => t.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const reordered = [...typeTickets];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    const next = {
      ...state,
      tickets: [
        ...reordered,
        ...state.tickets.filter((t) => t.type === "TRACK"),
        ...state.tickets.filter((t) => t.type === "ITEM"),
      ],
    };
    persist(next);
    toast.success("Tier order updated");
  };

  const moveTierByStep = (tierId, step) => {
    const tierList = state.tickets.filter((t) => t.type === "TIER");
    const index = tierList.findIndex((t) => t.id === tierId);
    if (index < 0) return;
    const targetIndex = index + step;
    if (targetIndex < 0 || targetIndex >= tierList.length) return;
    reorderByIds("TIER", tierId, tierList[targetIndex].id);
  };

  const addTicket = (type) => {
    const now = Date.now();
    if (type === "TIER" && !tierName.trim()) return toast.error("Tier name is required.");
    if (type === "TRACK" && !trackName.trim()) return toast.error("Track name is required.");
    if (type === "ITEM" && !itemName.trim()) return toast.error("Item name is required.");
    const endsAtIso = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    const startsAtIso = new Date().toISOString();
    const ticketName = type === "TIER" ? tierName.trim() : type === "TRACK" ? trackName.trim() : itemName.trim();
    const duplicateName = safeTickets.some((t) => t.type === type && t.name.trim().toLowerCase() === ticketName.toLowerCase());
    if (duplicateName) return toast.error(`${type} with same name already exists.`);
    const ticket = {
      id: `${type.toLowerCase()}_${now}`,
      type,
      name: ticketName,
      trackCode: type === "TRACK" ? trackCode.trim() || null : null,
      allowCrossSell: type === "TRACK" ? allowCrossSell : true,
      price: type === "TIER" ? 699 : 149,
      sold: 0,
      quota: 100,
      status: "Selling",
      purchaseLimitPerEmail: type === "ITEM" ? null : 1,
      startsAt: type === "ITEM" ? null : startsAtIso,
      endsAt: type === "ITEM" ? null : endsAtIso,
    };
    persist({ ...state, tickets: [...state.tickets, ticket] });
    toast.success(`${type} created`, { description: `${ticket.name} added.` });
    setTierName("");
    setTrackName("");
    setTrackCode("");
    setAllowCrossSell(false);
    setItemName("");
  };

  const removeTicket = (ticketId) => {
    const target = state.tickets.find((t) => t.id === ticketId);
    if (!window.confirm(`Delete "${target?.name || "this ticket"}"? This cannot be undone.`)) return;
    persist({ ...state, tickets: state.tickets.filter((t) => t.id !== ticketId) });
    toast.success("Ticket removed");
  };

  const toggleTrack = (ticketId, checked) => {
    persist({
      ...state,
      tickets: state.tickets.map((t) => (t.id === ticketId ? { ...t, allowCrossSell: checked } : t)),
    });
  };

  const addPromo = () => {
    if (!promoCode.trim()) return toast.error("Promo code is required.");
    const percent = Number(promoPercent);
    if (!Number.isFinite(percent) || percent <= 0 || percent > 100) {
      return toast.error("Promo percent must be between 1 and 100.");
    }
    if (promoLimit !== "") {
      const limitN = Number(promoLimit);
      if (!Number.isInteger(limitN) || limitN <= 0) {
        return toast.error("Promo limit must be a positive whole number.");
      }
    }
    const promo = {
      id: `promo_${Date.now()}`,
      code: promoCode.trim().toUpperCase(),
      discountPercent: percent,
      used: 0,
      limit: promoLimit === "" ? null : Number(promoLimit),
      startsAt: promoStartsAt ? new Date(promoStartsAt).toISOString() : null,
      endsAt: promoEndsAt ? new Date(promoEndsAt).toISOString() : null,
    };
    const duplicatePromo = safePromoCodes.some((p) => p.code.trim().toUpperCase() === promo.code);
    if (duplicatePromo) return toast.error("Promo code already exists.");
    if (promo.startsAt && promo.endsAt && new Date(promo.startsAt) > new Date(promo.endsAt)) {
      return toast.error("Promo start date must be before end date.");
    }
    persist({ ...state, promoCodes: [...state.promoCodes, promo] });
    setPromoCode("");
    setPromoPercent("10");
    setPromoLimit("");
    setPromoStartsAt("");
    setPromoEndsAt("");
    toast.success("Promo created");
  };

  const removePromo = (promoId) => {
    const target = state.promoCodes.find((p) => p.id === promoId);
    if (!window.confirm(`Delete promo "${target?.code || "this code"}"?`)) return;
    persist({ ...state, promoCodes: state.promoCodes.filter((p) => p.id !== promoId) });
    toast.success("Promo deleted");
  };

  const hasTierInput = tierName.trim().length > 0;
  const hasTrackInput = trackName.trim().length > 0;
  const hasItemInput = itemName.trim().length > 0;
  const hasPromoInput = promoCode.trim().length > 0;
  const promoPercentNum = Number(promoPercent);
  const promoPercentValid = Number.isFinite(promoPercentNum) && promoPercentNum > 0 && promoPercentNum <= 100;
  const promoDateValid = !(promoStartsAt && promoEndsAt) || new Date(promoStartsAt) <= new Date(promoEndsAt);
  const promoCanSubmit = hasPromoInput && promoPercentValid && promoDateValid;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto space-y-8 md:space-y-10" data-testid="tickets-page">
      <div className="flex items-end justify-between flex-wrap gap-4 pb-6 border-b border-[var(--border-default)]">
        <div>
          <div className="text-overline">{e.title} · ticketing</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Ticket engine</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">Clean setup for tiers, tracks, related items, and promo logic.</p>
          {isLoading ? <p className="text-xs text-[var(--text-tertiary)] mt-2">Syncing ticket engine…</p> : null}
          {!isLoading && dirty ? <p className="text-xs text-[var(--primary)] mt-2">Saved</p> : null}
          {syncError ? <p className="text-xs text-[var(--error)] mt-2">{syncError}</p> : null}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Jump id="tiers-section" label="Tiers" />
          <Jump id="tracks-section" label="Tracks" />
          <Jump id="items-section" label="Items" />
          <Jump id="promo-codes-section" label="Promo codes" icon={<Tag size={14} />} />
          <button
            className="btn-secondary"
            onClick={async () => {
              setSyncError("");
              setIsLoading(true);
              try {
                await syncTicketEngine(e.id);
                setState(loadTicketEngine(e.id));
                toast.success("Ticket engine refreshed");
              } catch {
                setSyncError("Refresh failed. API may be unreachable.");
                toast.error("Could not refresh ticket engine");
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI k="Gross sold (live purchases)" v={`$${liveGross.toLocaleString()}`} />
        <KPI k="Tickets issued (live purchases)" v={purchaseCount.toLocaleString()} />
        <KPI k="Sell-through (live purchases)" v={`${liveSellThrough}%`} />
      </div>
      <div className="text-xs text-[var(--text-tertiary)] font-mono">
        Baseline engine totals: ${totalSold.toLocaleString()} gross · {totalIssued.toLocaleString()} issued.
        {seededBaseline ? " Live purchase ledger is currently empty; baseline likely comes from seed/default values." : ""}
      </div>

      <SectionTitle id="tiers-section" title="Tiers" />
      <div className={`${CARD_CLASS} p-4 flex flex-col md:flex-row gap-2`}>
        <input className={INPUT_CLASS} value={tierName} onChange={(evt) => setTierName(evt.target.value)} placeholder="Early Bird / Regular / VIP" />
        <button className="btn-primary disabled:opacity-50 md:w-auto w-full" disabled={!hasTierInput || isLoading} onClick={() => addTicket("TIER")}>
          <Plus size={14} /> Add Tier
        </button>
      </div>
      <div className="space-y-3">
        {tiers.map((ticket) => (
          <TicketRow
            key={ticket.id}
            ticket={ticket}
            tiers={tiers}
            editingId={editingId}
            setEditingId={setEditingId}
            removeTicket={removeTicket}
            updateTicket={updateTicket}
            setDraggingTierId={setDraggingTierId}
            draggingTierId={draggingTierId}
            onTierDrop={reorderByIds}
            dropTierId={dropTierId}
            setDropTierId={setDropTierId}
            moveTierByStep={moveTierByStep}
            tracks={tracks}
          />
        ))}
        {tiers.length === 0 ? <div className={`${CARD_CLASS} p-6 text-sm text-[var(--text-tertiary)]`}>No tiers yet. Add at least one tier so registration can proceed.</div> : null}
      </div>

      <SectionTitle id="tracks-section" title="Tracks" />
      <div className={`${CARD_CLASS} p-4 grid md:grid-cols-4 gap-2 items-center`}>
        <input className={INPUT_CLASS} value={trackName} onChange={(evt) => setTrackName(evt.target.value)} placeholder="Track name" />
        <input className={INPUT_CLASS} value={trackCode} onChange={(evt) => setTrackCode(evt.target.value)} placeholder="Track code (optional)" />
        <label className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
          <input type="checkbox" checked={allowCrossSell} onChange={(evt) => setAllowCrossSell(evt.target.checked)} />
          Same email can buy other tracks/items
        </label>
        <button className="btn-primary disabled:opacity-50 md:w-auto w-full" disabled={!hasTrackInput || isLoading} onClick={() => addTicket("TRACK")}>
          <Plus size={14} /> Add Track
        </button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {tracks.map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            editingId={editingId}
            setEditingId={setEditingId}
            removeTicket={removeTicket}
            updateTicket={updateTicket}
            tiers={tiers}
            tracks={tracks}
            extra={
              <label className="text-xs text-[var(--text-secondary)] mt-3 flex items-center gap-2">
                <input type="checkbox" checked={!!ticket.allowCrossSell} onChange={(evt) => toggleTrack(ticket.id, evt.target.checked)} />
                Same email can buy other tracks/items
              </label>
            }
          />
        ))}
        {tracks.length === 0 ? <div className={`${CARD_CLASS} p-6 text-sm text-[var(--text-tertiary)]`}>No tracks configured.</div> : null}
      </div>

      <SectionTitle id="items-section" title="Related items" />
      <div className={`${CARD_CLASS} p-4 flex flex-col md:flex-row gap-2`}>
        <input className={INPUT_CLASS} value={itemName} onChange={(evt) => setItemName(evt.target.value)} placeholder="Workshop / Dinner / Add-on item" />
        <button className="btn-primary disabled:opacity-50 md:w-auto w-full" disabled={!hasItemInput || isLoading} onClick={() => addTicket("ITEM")}>
          <Plus size={14} /> Add Item
        </button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            editingId={editingId}
            setEditingId={setEditingId}
            removeTicket={removeTicket}
            updateTicket={updateTicket}
            tiers={tiers}
            tracks={tracks}
          />
        ))}
        {items.length === 0 ? <div className={`${CARD_CLASS} p-6 text-sm text-[var(--text-tertiary)]`}>No related items configured.</div> : null}
      </div>

      <SectionTitle id="promo-codes-section" title="Promo codes" />
      <div className={`${CARD_CLASS} p-4 grid grid-cols-2 md:grid-cols-6 gap-2`}>
        <input className={INPUT_CLASS} value={promoCode} onChange={(evt) => setPromoCode(evt.target.value)} placeholder="Code e.g. SAVE20" />
        <input className={INPUT_CLASS} value={promoPercent} onChange={(evt) => setPromoPercent(evt.target.value)} placeholder="% off" />
        <input className={INPUT_CLASS} value={promoLimit} onChange={(evt) => setPromoLimit(evt.target.value)} placeholder="Limit" />
        <input className={INPUT_CLASS} type="date" value={promoStartsAt} onChange={(evt) => setPromoStartsAt(evt.target.value)} />
        <input className={INPUT_CLASS} type="date" value={promoEndsAt} onChange={(evt) => setPromoEndsAt(evt.target.value)} />
        <button className="btn-primary disabled:opacity-50 col-span-2 md:col-span-1" disabled={!promoCanSubmit || isLoading} onClick={addPromo}>
          <Plus size={14} /> Add Promo
        </button>
      </div>
      {!promoPercentValid ? <p className="text-xs text-red-400 -mt-7">Discount must be between 1 and 100.</p> : null}
      {!promoDateValid ? <p className="text-xs text-red-400 -mt-7">Promo start date must be before end date.</p> : null}

      <div className={`${CARD_CLASS} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Code</th>
              <th className="p-4 text-left">Discount</th>
              <th className="p-4 text-left">Scope</th>
              <th className="p-4 text-right">Used</th>
              <th className="p-4 text-right">Limit</th>
              <th className="p-4 text-right">Window</th>
              <th className="p-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {safePromoCodes.map((promo) => (
              <tr key={promo.id} className="border-b border-[var(--border-default)]">
                <td className="p-4 font-mono">{promo.code}</td>
                <td className="p-4">{promo.discountPercent}% off</td>
                <td className="p-4 text-[var(--text-secondary)]">All tickets</td>
                <td className="p-4 text-right font-mono">{promo.used}</td>
                <td className="p-4 text-right font-mono">{promo.limit ?? "—"}</td>
                <td className="p-4 text-right font-mono">
                  {promo.startsAt ? String(promo.startsAt).slice(0, 10) : "—"} → {promo.endsAt ? String(promo.endsAt).slice(0, 10) : "—"}
                </td>
                <td className="p-4 text-right">
                  <button className="btn-ghost text-xs" onClick={() => removePromo(promo.id)}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {safePromoCodes.length === 0 ? (
              <tr>
                <td className="p-4 text-[var(--text-tertiary)]" colSpan={7}>
                  No promo codes yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Jump({ id, label, icon = null }) {
  return (
    <button className="btn-secondary" onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })}>
      {icon}
      {label}
    </button>
  );
}

function SectionTitle({ id, title }) {
  return (
    <h2 id={id} className="font-heading text-2xl tracking-tight">
      {title}
    </h2>
  );
}

function TicketRow({
  ticket,
  tiers,
  editingId,
  setEditingId,
  removeTicket,
  updateTicket,
  draggingTierId,
  setDraggingTierId,
  onTierDrop,
  dropTierId,
  setDropTierId,
  moveTierByStep,
  tracks,
}) {
  const pct = (ticket.sold / Math.max(1, ticket.quota)) * 100;
  const displayStatus = getDisplayStatus(ticket);
  const sold = displayStatus === "Sold out";
  const statusColor = displayStatus === "Expired" ? "#E25D3A" : sold ? "#71717A" : displayStatus === "Scheduled" ? "#60A5FA" : "var(--primary)";
  const rowActive = dropTierId === ticket.id && draggingTierId !== ticket.id;
  const index = tiers.findIndex((t) => t.id === ticket.id);
  return (
    <div
      className={`${CARD_CLASS} p-4 md:p-6 transition ${rowActive ? "ring-1 ring-[var(--primary)]" : ""}`}
      data-testid={`tier-${ticket.id}`}
      onDragOver={(evt) => evt.preventDefault()}
      onDragEnter={() => setDropTierId(ticket.id)}
      onDragLeave={() => setDropTierId((prev) => (prev === ticket.id ? null : prev))}
      onDrop={() => {
        if (draggingTierId) onTierDrop("TIER", draggingTierId, ticket.id);
        setDraggingTierId(null);
        setDropTierId(null);
      }}
    >
      {rowActive ? <div className="mb-3 h-0.5 bg-[var(--primary)]" /> : null}
      <div className="grid gap-4 md:grid-cols-12 md:items-center md:min-h-[56px]">
        <button
          className="hidden md:inline-flex md:col-span-1 md:order-1 text-[var(--text-secondary)] cursor-grab active:cursor-grabbing btn-ghost h-10 w-10 items-center justify-center hover:bg-[var(--bg-elev)]"
          draggable
          aria-label={`Drag tier ${ticket.name} to reorder`}
          title="Drag to reorder tier"
          onDragStart={() => setDraggingTierId(ticket.id)}
          onDragEnd={() => {
            setDraggingTierId(null);
            setDropTierId(null);
          }}
        >
          <GripVertical size={18} />
        </button>
        <div className="flex items-start justify-between gap-3 md:contents">
          <div className="md:col-span-3 md:order-2 min-w-0">
            <div className="font-heading text-lg">{ticket.name}</div>
            <div className="text-xs text-[var(--text-tertiary)] font-mono mt-1">{formatWindow(ticket.startsAt, ticket.endsAt)}</div>
          </div>
          <div className="shrink-0 md:col-span-2 md:order-5 md:flex md:justify-start">
            <span
              className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border whitespace-nowrap inline-flex min-w-[76px] justify-center"
              style={{ color: statusColor, borderColor: statusColor + "55" }}
            >
              {displayStatus}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 md:contents">
          <div className="shrink-0 md:col-span-1 md:order-3 font-mono text-2xl">${ticket.price}</div>
          <div className="flex-1 min-w-0 md:col-span-3 md:order-4">
            <div className="text-xs font-mono">
              {ticket.sold.toLocaleString()} / {ticket.quota.toLocaleString()}
            </div>
            <div className="mt-2 h-1 bg-[var(--bg-elev)]">
              <div className="h-full" style={{ width: `${pct}%`, background: sold ? "#71717A" : "var(--primary)" }} />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 md:col-span-2 md:order-6 md:justify-end">
          <button
            className="btn-ghost text-xs h-10 w-10 inline-flex items-center justify-center hover:bg-[var(--bg-elev)]"
            onClick={() => moveTierByStep(ticket.id, -1)}
            disabled={index === 0}
            title="Move up"
            aria-label={`Move ${ticket.name} up`}
          >
            <ChevronUp size={16} />
          </button>
          <button
            className="btn-ghost text-xs h-10 w-10 inline-flex items-center justify-center hover:bg-[var(--bg-elev)]"
            onClick={() => moveTierByStep(ticket.id, 1)}
            disabled={index === tiers.length - 1}
            title="Move down"
            aria-label={`Move ${ticket.name} down`}
          >
            <ChevronDown size={16} />
          </button>
          <button className="btn-ghost text-xs h-10 px-3 inline-flex items-center gap-1 hover:bg-[var(--bg-elev)]" aria-label={`Edit settings for ${ticket.name}`} title="Edit" onClick={() => setEditingId(editingId === ticket.id ? null : ticket.id)}>
            <Settings2 size={14} /> Edit
          </button>
          <button className="btn-ghost text-xs h-10 px-3 inline-flex items-center gap-1 hover:bg-[var(--bg-elev)]" aria-label={`Delete ${ticket.name}`} title="Delete" onClick={() => removeTicket(ticket.id)}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
      {editingId === ticket.id ? <SettingsGrid ticket={ticket} updateTicket={updateTicket} tiers={tiers} tracks={tracks} /> : null}
    </div>
  );
}

function TicketCard({ ticket, editingId, setEditingId, removeTicket, updateTicket, tiers = [], tracks = [], extra = null }) {
  const displayStatus = getDisplayStatus(ticket);
  const statusColor = displayStatus === "Expired" ? "#E25D3A" : displayStatus === "Sold out" ? "#71717A" : "var(--primary)";
  return (
    <div className={`${CARD_CLASS} p-6`}>
      <div className="text-overline">{ticket.type === "TRACK" ? "Track" : "Item"}</div>
      <div className="font-heading text-lg mt-2">{ticket.name}</div>
      {ticket.type === "TRACK" ? <div className="text-xs text-[var(--text-secondary)] mt-1">Code: {ticket.trackCode || "-"}</div> : null}
      {ticket.type === "ITEM" ? <div className="text-xs text-[var(--text-secondary)] mt-1">Window: {formatWindow(ticket.startsAt, ticket.endsAt)}</div> : null}
      {extra}
      <div className="flex items-end justify-between mt-4">
        <div className="font-mono text-3xl">${ticket.price}</div>
        <div className="text-xs font-mono" style={{ color: statusColor }}>{displayStatus}</div>
      </div>
      <div className="mt-4 flex gap-2">
        <button className="btn-ghost text-xs h-10 px-3 inline-flex items-center hover:bg-[var(--bg-elev)]" aria-expanded={editingId === ticket.id} aria-label={`Edit settings for ${ticket.name}`} onClick={() => setEditingId(editingId === ticket.id ? null : ticket.id)}>
          Edit
        </button>
        <button className="btn-ghost text-xs h-10 px-3 inline-flex items-center hover:bg-[var(--bg-elev)]" aria-label={`Delete ${ticket.name}`} onClick={() => removeTicket(ticket.id)}>
          Delete
        </button>
      </div>
      {editingId === ticket.id ? <SettingsGrid ticket={ticket} updateTicket={updateTicket} tiers={tiers} tracks={tracks} /> : null}
    </div>
  );
}

function SettingsGrid({ ticket, updateTicket, tiers = [], tracks = [] }) {
  const isIndefiniteItem = ticket.type === "ITEM" && !ticket.startsAt && !ticket.endsAt;
  const dateDisabled = ticket.type === "ITEM" && isIndefiniteItem;
  const saleWindowInvalid =
    ticket.startsAt && ticket.endsAt && new Date(ticket.startsAt).getTime() > new Date(ticket.endsAt).getTime();
  return (
    <div className="mt-4 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 space-y-4">
      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Status</span>
          <select
            className={INPUT_CLASS}
            value={ticket.status || "Selling"}
            onChange={(evt) => updateTicket(ticket.id, { status: evt.target.value })}
          >
            <option value="Selling">Selling</option>
            <option value="Draft">Draft</option>
            <option value="Hidden">Hidden</option>
            <option value="Sold out">Sold out</option>
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Price</span>
          <input className={INPUT_CLASS} type="number" value={ticket.price} onChange={(evt) => updateTicket(ticket.id, { price: Number(evt.target.value) || 0 })} />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Quantity</span>
          <input className={INPUT_CLASS} type="number" value={ticket.quota} onChange={(evt) => updateTicket(ticket.id, { quota: Number(evt.target.value) || 0 })} />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Per-email limit</span>
          <input
            className={INPUT_CLASS}
            type="number"
            value={ticket.purchaseLimitPerEmail ?? ""}
            onChange={(evt) => updateTicket(ticket.id, { purchaseLimitPerEmail: evt.target.value === "" ? null : Number(evt.target.value) })}
          />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Starts</span>
          <input
            className={INPUT_CLASS}
            type="date"
            value={String(ticket.startsAt || "").slice(0, 10)}
            disabled={dateDisabled}
            onChange={(evt) => updateTicket(ticket.id, { startsAt: toIsoOrNull(evt.target.value) })}
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Ends</span>
          <input
            className={INPUT_CLASS}
            type="date"
            value={String(ticket.endsAt || "").slice(0, 10)}
            disabled={dateDisabled}
            onChange={(evt) => updateTicket(ticket.id, { endsAt: toIsoOrNull(evt.target.value) })}
          />
        </label>
      </div>
      {ticket.type === "ITEM" ? (
        <>
          <div className="grid grid-cols-2 gap-2 border-t border-[var(--border-default)] pt-4">
            <label className="space-y-1">
              <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Base type</span>
              <select
                className={INPUT_CLASS}
                value={ticket.baseType || ""}
                onChange={(evt) => updateTicket(ticket.id, { baseType: evt.target.value || null, baseRef: null })}
              >
                <option value="">Optional</option>
                <option value="TIER">Attach to tier</option>
                <option value="TRACK">Attach to track</option>
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Base target</span>
              <select
                className={INPUT_CLASS}
                value={ticket.baseRef || ""}
                onChange={(evt) => updateTicket(ticket.id, { baseRef: evt.target.value || null })}
                disabled={!ticket.baseType}
              >
                <option value="">—</option>
                {(ticket.baseType === "TRACK" ? tracks : tiers).map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="text-xs text-[var(--text-secondary)] flex items-center gap-2">
            <input
              type="checkbox"
              checked={isIndefiniteItem}
              onChange={(evt) =>
                updateTicket(ticket.id, evt.target.checked ? { startsAt: null, endsAt: null } : { startsAt: new Date().toISOString(), endsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() })
              }
            />
            Indefinite availability (no start/end date)
          </label>
        </>
      ) : null}
      {saleWindowInvalid ? <p className="text-xs text-[var(--error)]">Start date must be before end date.</p> : null}
    </div>
  );
}

function KPI({ k, v }) {
  return (
    <div className={`${CARD_CLASS} p-6`}>
      <div className="text-overline">{k}</div>
      <div className="font-mono text-3xl mt-2">{v}</div>
    </div>
  );
}

function toIsoOrNull(value) {
  return value ? new Date(value).toISOString() : null;
}

function formatWindow(startsAt, endsAt) {
  if (!startsAt && !endsAt) return "Indefinite";
  const start = startsAt ? String(startsAt).slice(0, 10) : "—";
  const end = endsAt ? String(endsAt).slice(0, 10) : "—";
  return `${start} → ${end}`;
}

function getDisplayStatus(ticket) {
  const now = Date.now();
  const configuredStatus = String(ticket.status || "Selling");
  if (configuredStatus === "Hidden" || configuredStatus === "Draft") return configuredStatus;
  if (ticket.endsAt && now > new Date(ticket.endsAt).getTime()) return "Expired";
  const quota = Number(ticket.quota || 0);
  if (quota > 0 && Number(ticket.sold || 0) >= quota) return "Sold out";
  if (ticket.startsAt && now < new Date(ticket.startsAt).getTime()) return "Scheduled";
  return configuredStatus;
}
